import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import {
  useForm,
  useFieldArray,
  useFormContext,
  Controller,
  useWatch,
} from "react-hook-form";
import {estimateShell, estimateNozzle} from "../../../Ams/PV3/API`s/pv/sqliteApi";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import DeleteIcon from "@mui/icons-material/Delete";
import axios, { secondAxios } from "../../../../../api/axios";
import CustomAlert from "../../../../../components/widgets/CustomAlert";
import { Stack } from "@mui/material";
import EstimationTable from "./EstimationTable";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { getCostComparison, getMaterialAvailability } from "./api";
import DetailsDialog from "./DetailsDialog";
import BomTable from "./BomTable";
import { costComparisonColumns } from "./dialogTableColumns";
import { warehouseColumns } from "../../../SCMPage/MCSTables";
import SummaryBox from "./SummaryBox";
import { initialSummaryData } from "./utils";
import NavTabs from "../../../../../components/Tabs/NavTabs";
import { PVContext } from "../../../Ams/PV3/PressureVesselData/PVContext";
import {
  computeShellUID,
  computeNozzleUID,
} from "../../../../../utils/pvUID";

const attachmentComponents = [
  "Saddle",
  "Wear Plate",
  "Skirt",
  "Leg",
  "Lifting Lug",
  "Vortex Breaker",
  "Manway Davit",
  "Hydrotest",
  "Internal Paint",
  "External Paint",
];

const attachmentUnitOptions = ["Nos", "Kg", "mm^2", "mm^3"];

const attachmentDbConfig = {
  Saddle: {
    table: "Saddle SA 36",
    keyColumnMetric: "Dia-Height (mm)",
    weightColumnMetric: "Weight (Kg)",
    costColumnMetric: "Material cost ($)",
    manhoursColumn: "Manhours",
  },
  "Lifting Lug": {
    table: "Lifting Lug SA 516 70",
    keyColumnMetric: "Plate Thk (mm)",
    weightColumnMetric: "Weight kg",
    costColumnMetric: "Materia cost USD",
    manhoursColumn: "Manhours",
  },
  "Vortex Breaker": {
    table: "Vortex Breaker SA 516 70",
    keyColumnMetric: "Nozzle Size",
    weightColumnMetric: "Weight Kg",
    costColumnMetric: "Material Cost USD",
    manhoursColumn: "Manhours",
  },
  "Manway Davit": {
    table: "Manway Davit SA 106 B",
    keyColumnMetric: "Nozzle Size Sch",
    weightColumnMetric: "Weight",
    costColumnMetric: "Material Cost USD",
    manhoursColumn: "Manhours",
  },
};

const attachmentTableProps = {
  muiTableHeadCellProps: {
    sx: {
      backgroundColor: "hsla(250, 86%, 97%, 1)",
      borderRight: "1px solid rgba(224,224,224,1)",
      borderTop: "1px solid rgba(224,224,224,1)",
      "& .Mui-TableHeadCell-Content-Wrapper": {
        whiteSpace: "normal",
        justifyContent: "center",
        marginBottom: "0.4px",
      },
      fontSize: "0.7rem",
      verticalAlign: "middle",
      height: "40.4px",
    },
  },
  muiTableProps: {
    size: "small",
    sx: {
      "& .MuiInputBase-root": {
        fontSize: "0.7rem",
        borderRadius: 0,
      },
      "& .MuiButtonBase-root": {
        fontSize: "0.7rem",
      },
    },
  },
  muiTableBodyCellProps: {
    sx: {
      borderRight: "1px solid rgba(224,224,224,1)",
      padding: "0rem",
      paddingLeft: "5px",
    },
  },
};

const getAttachmentColumns = ({
  handleAttachmentComponentChange,
  handleAttachmentReferenceChange,
  handleAttachmentCellChange,
}) => [
  {
    header: "Component",
    accessorKey: "component",
    Cell: ({ row }) => (
      <Select
        value={row.original.component ?? ""}
        onChange={(e) =>
          handleAttachmentComponentChange(row.index, e.target.value)
        }
        fullWidth
        size="small"
      >
        {attachmentComponents.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    ),
  },
  {
    header: "Reference",
    accessorKey: "reference",
    Cell: ({ row }) => {
      const r = row.original || {};
      const hasOptions =
        r.referenceOptions && r.referenceOptions.length > 0;
      if (hasOptions) {
        return (
          <Select
            value={r.reference ?? ""}
            onChange={(e) =>
              handleAttachmentReferenceChange(
                row.index,
                r.component,
                e.target.value
              )
            }
            fullWidth
            size="small"
          >
            {r.referenceOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        );
      }
      return (
        <input
          value={r.reference ?? ""}
          onChange={(e) =>
            handleAttachmentCellChange(
              row.index,
              "reference",
              e.target.value
            )
          }
          style={{ width: "100%" }}
        />
      );
    },
  },
  {
    header: "Qty",
    accessorKey: "qty",
    Cell: ({ row }) => (
      <input
        type="number"
        value={row.original.qty ?? ""}
        onChange={(e) =>
          handleAttachmentCellChange(row.index, "qty", e.target.value)
        }
        style={{ width: "100%" }}
      />
    ),
  },
  {
    header: "Unit",
    accessorKey: "unit",
    Cell: ({ row }) => (
      <Select
        value={row.original.unit ?? "Nos"}
        onChange={(e) =>
          handleAttachmentCellChange(row.index, "unit", e.target.value)
        }
        fullWidth
        size="small"
      >
        {attachmentUnitOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    ),
  },
  {
    header: "Unit Weight (Kg)",
    accessorKey: "totalWeight",
    Cell: ({ row }) => (
      <input
        type="number"
        value={row.original.totalWeight ?? ""}
        onChange={(e) =>
          handleAttachmentCellChange(
            row.index,
            "totalWeight",
            e.target.value
          )
        }
        style={{ width: "100%" }}
      />
    ),
  },
  {
    header: "Material Cost (US $)",
    accessorKey: "totalMaterialCost",
    Cell: ({ row }) => (
      <input
        type="number"
        value={row.original.totalMaterialCost ?? ""}
        onChange={(e) =>
          handleAttachmentCellChange(
            row.index,
            "totalMaterialCost",
            e.target.value
          )
        }
        style={{ width: "100%" }}
      />
    ),
  },
  {
    header: "Total Manhours",
    accessorKey: "totalManhours",
    Cell: ({ row }) => (
      <input
        type="number"
        value={row.original.totalManhours ?? ""}
        onChange={(e) =>
          handleAttachmentCellChange(
            row.index,
            "totalManhours",
            e.target.value
          )
        }
        style={{ width: "100%" }}
      />
    ),
  },
  {
    header: "Remarks",
    accessorKey: "remarks",
    Cell: ({ row }) => (
      <input
        value={row.original.remarks ?? ""}
        onChange={(e) =>
          handleAttachmentCellChange(row.index, "remarks", e.target.value)
        }
        style={{ width: "100%" }}
      />
    ),
  },
];

//The Main Function
const EstimationTab = (props) => {
  document.title = "Pressure Vessel - Estimation";
  const pvContext = useContext(PVContext);
  const currentRevision = pvContext?.currentRevision ?? "default";
  const revisionLoadVersion = pvContext?.revisionLoadVersion ?? 0;
  const estimationRefreshKey = `${currentRevision}-${revisionLoadVersion}`;

  const { reRender } = props;
  const {
    register,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();
  const { id: projectId } = useParams();
  const { append, update, remove } = useFieldArray({
    control,
    name: "DataGridView8",
  });
  const watchedDgv8 = useWatch({ control, name: "DataGridView8", defaultValue: [] });
  const watchedShellEstRows = useWatch({
    control,
    name: "DataGridView7",
    defaultValue: [],
  });
  const watchedNozzleEstRows = useWatch({
    control,
    name: "DataGridView2",
    defaultValue: [],
  });
  const watchedTotalMaterialCost = useWatch({ control, name: "totalMaterialCost", defaultValue: 0 });
  const watchedTotalHours = useWatch({ control, name: "totalHours", defaultValue: 0 });
  const watchedTextBox35 = useWatch({ control, name: "TextBox35", defaultValue: 0 });
  const autoBuildKeyRef = useRef("");
  const estimationTableRows = useMemo(() => {
    const rows = Array.isArray(watchedDgv8) ? watchedDgv8 : [];
    return rows.map((row, index) => ({
      ...row,
      __rowId: `${index}-${row?.Description ?? ""}-${row?.Qty ?? ""}`,
    }));
  }, [watchedDgv8]);
  const buildSummaryFromForm = () => {
    const total_cost = roundToTwo(getValues("totalMaterialCost") || 0);
    const totalHours = getValues("totalHours");
    const fabRate = getValues("TextBox35");
    const fab_cost = roundToTwo(totalHours * fabRate || 0);
    if (Number.isFinite(totalHours) && Number.isFinite(total_cost)) {
      return [
        {
          title: "Total Material Cost",
          estimation: total_cost,
          percentage: 10,
          total: roundToTwo(total_cost + total_cost / 10),
        },
        {
          title: "Total FAB Cost",
          estimation: fab_cost,
          percentage: 10,
          total: roundToTwo(fab_cost + fab_cost / 10),
        },
      ];
    }
    return [];
  };

  const buildBomFromDgv8 = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    return rows.map((row, index) => {
      const qtyStr = String(row.Qty ?? "").trim();
      const isDimensionQty = /\bx\b/i.test(qtyStr);
      let qty = row.Qty;
      let dimension = "-";
      let unit = "Nos";
      if (isDimensionQty) {
        const parts = qtyStr.split(/\s*x\s*/i).filter(Boolean);
        if (parts.length >= 2) {
          dimension = `${parts[0]} x ${parts[1]}`;
        } else {
          dimension = qtyStr;
        }
        qty = 1;
        unit = "m";
      }
      const weightKg = parseFloat(row["Measure/Wt"]) || 0;
      const weightLb = weightKg * 2.20462;
      return {
        itemNo: index + 1,
        materialDescription: row.Description || "",
        qty: qty,
        dimension: dimension,
        unit: unit,
        weightLb: Math.round(weightLb * 100) / 100,
      };
    });
  };

  const [summaryData, setSummaryData] = useState(() => buildSummaryFromForm());
  const [isLoading, setIsLoading] = useState(false);
  const [isEditTable, setIsEditTable] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [AlertContent, setAlertContent] = useState({
    severity: "",
    message: "",
  });

  const [attachmentRows, setAttachmentRows] = useState([]);
  const [attachmentBomRows, setAttachmentBomRows] = useState([]);
  const [pvTables, setPvTables] = useState(null);
  const [bomData, setBomData] = useState([]);

  // Sync bomData from form state whenever revision data is loaded/reloaded
  useEffect(() => {
    const saved = getValues("bomData");
    if (Array.isArray(saved)) {
      setBomData(saved);
    }
  }, [estimationRefreshKey, getValues]);

  const defaultAttachmentRow = {
    component: "",
    reference: "",
    qty: 1,
    unit: "Nos",
    totalWeight: "",
    totalMaterialCost: "",
    totalManhours: "",
    remarks: "",
    referenceOptions: [],
  };

  const toNearestPowerOfTwoFraction = (x) => {
    if (!Number.isFinite(x)) return String(x);

    const sign = x < 0 ? "-" : "";
    const a = Math.abs(x);
    const whole = Math.floor(a);
    const frac = a - whole;

    let n16 = Math.round(frac * 16 + Number.EPSILON);
    let W = whole;
    if (n16 === 16) {
      W += 1;
      n16 = 0;
    }

    if (n16 === 0) {
      if (W === 0) return "0";
      return sign + String(W);
    }

    let num = n16;
    let den = 16;
    while (den > 2 && num % 2 === 0) {
      num /= 2;
      den /= 2;
    }

    if (W === 0) return `${sign}${num}/${den}`;
    return `${sign}${W} ${num}/${den}`;
  };

  const groupNozzleItems = (items) => {
    const map = new Map();
    items.forEach((item) => {
      const key = `${item.Component}||${item.Description}`;
      const existing = map.get(key);
      if (existing) {
        existing.Qty += item.Qty || 1;
        existing["Measure/Wt"] =
          (existing["Measure/Wt"] || 0) + (item["Measure/Wt"] || 0);
        existing["FAB Hours"] =
          (Number(existing["FAB Hours"]) || 0) +
          (Number(item["FAB Hours"]) || 0);
      } else {
        map.set(key, { ...item });
      }
    });
    return Array.from(map.values());
  };

  const mmToInch = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num) || num === 0) return 0;
    return num / 25.4;
  };

  const safeNumber = (val) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : 0;
  };

  const getPvCostTables = async () => {
    if (
      typeof window === "undefined" ||
      !window.electron ||
      !window.electron.db ||
      !window.electron.db.getAllTables
    ) {
      return {};
    }

    try {
      const res = await window.electron.db.getAllTables("pv");
      if (!res || typeof res !== "object") return {};
      return res;
    } catch {
      return {};
    }
  };

  const loadPvTables = async () => {
    if (pvTables) return pvTables;
    const tables = await getPvCostTables();
    const safeTables = tables || {};
    setPvTables(safeTables);
    return safeTables;
  };

  const getPipeUnitCostFromTables = (tables, size, sch) => {
    if (!tables || !tables["Pipe Cost"] || !size || !sch) return 0;

    const table = tables["Pipe Cost"] || [];

    const normalizeSchedule = (value) => {
      const raw = String(value).trim();
      if (!raw) return raw;
      const lower = raw.toLowerCase();
      if (lower.startsWith("sch")) {
        const parts = raw.split(/[\s.]+/).filter(Boolean);
        const last = parts[parts.length - 1];
        return last || raw;
      }
      return raw;
    };

    const scheduleKey = normalizeSchedule(sch);
    const row =
      table.find((r) => normalizeSchedule(r.Schedule) === scheduleKey) || null;
    if (!row) return 0;

    const sizeKeyRaw = String(size).trim();
    let val = Number(row[sizeKeyRaw]);

    if (!Number.isFinite(val)) {
      const numSize = Number(size);
      if (Number.isFinite(numSize) && numSize > 0) {
        const fracKey = String(toNearestPowerOfTwoFraction(numSize)).trim();
        val = Number(row[fracKey]);
      }
    }

    return Number.isFinite(val) ? val : 0;
  };

  const getFlangeUnitCostFromTables = (tables, size, sch, rating) => {
    if (!tables || !tables["Flange WN Cost"] || !size || !sch || !rating) {
      return 0;
    }

    const table = tables["Flange WN Cost"] || [];

    const normalizeSchedule = (value) => {
      const raw = String(value).trim();
      if (!raw) return raw;
      const lower = raw.toLowerCase();
      if (lower.startsWith("sch")) {
        const parts = raw.split(/[\s.]+/).filter(Boolean);
        const last = parts[parts.length - 1];
        return last || raw;
      }
      return raw;
    };

    const schedulePart = normalizeSchedule(sch);

    let sizeLabel = String(size).trim();
    if (!sizeLabel) return 0;

    const numericSize = Number(size);
    if (Number.isFinite(numericSize) && numericSize > 0) {
      sizeLabel = String(toNearestPowerOfTwoFraction(numericSize)).trim();
    }

    const key = `${sizeLabel}-${schedulePart}`;
    const row =
      table.find((r) => String(r.SizeAndSchedule).trim() === key) || null;
    if (!row) return 0;

    const ratingNumber = Number(rating);
    const ratingKey =
      Number.isFinite(ratingNumber) && ratingNumber > 0
        ? String(ratingNumber)
        : String(rating).trim();

    const val = Number(row[ratingKey]);
    return Number.isFinite(val) ? val : 0;
  };

  const getMaterialCostFromTables = (tables, component, material) => {
    if (!tables || !tables["material cost"] || !component || !material)
      return 0;
    const table = tables["material cost"] || [];
    const row = table.find(
      (r) =>
        String(r.Component).toLowerCase() ===
          String(component).toLowerCase() &&
        String(r.Material).toLowerCase() === String(material).toLowerCase()
    );
    return row ? Number(row["Material Cost"]) || 0 : 0;
  };

  const getFabRateFromTables = (tables, component, size) => {
    if (!tables || !tables["fab_rate_hr"]) return 74;
    const table = tables["fab_rate_hr"] || [];

    let key = "";
    if (
      typeof component === "string" &&
      component.toLowerCase().includes("shell")
    ) {
      key = "Shell";
    } else if (
      typeof component === "string" &&
      component.toLowerCase().includes("head")
    ) {
      key = "Head";
    } else if (
      typeof component === "string" &&
      component.toLowerCase().includes("pipe")
    ) {
      const numericSize = Number(size);
      if (Number.isFinite(numericSize) && numericSize > 0) {
        key = `Pipe ${numericSize}"`;
      }
    } else if (
      typeof component === "string" &&
      component.toLowerCase().includes("pad")
    ) {
      const numericSize = Number(size);
      if (Number.isFinite(numericSize) && numericSize > 0) {
        key = `Pad ${numericSize}"`;
      }
    }

    if (key) {
      const row = table.find(
        (r) => String(r.Components).trim() === key
      );
      if (row) return Number(row["Fab Rate / Hr"]) || 74;
    }

    return 74;
  };

  // Update summary when cost values change
  useEffect(() => {
    setSummaryData(buildSummaryFromForm());
  }, [watchedTotalMaterialCost, watchedTotalHours, watchedTextBox35]);

  // BOM is now only generated on-demand via the BOM button
  // useEffect(() => {
  //   console.log("[EstimationTab] BOM auto-gen:", { watchedDgv8Len: watchedDgv8?.length });
  //   setBomData(buildBomFromDgv8(watchedDgv8));
  // }, [watchedDgv8]);

  // Build DGV8 once the revision data has actually finished loading.
  useEffect(() => {
    const dgv8Len = Array.isArray(watchedDgv8) ? watchedDgv8.length : 0;
    const shellEstRowsLen = Array.isArray(watchedShellEstRows)
      ? watchedShellEstRows.length
      : 0;
    const nozzleEstRowsLen = Array.isArray(watchedNozzleEstRows)
      ? watchedNozzleEstRows.length
      : 0;
    const hasEstimateInputs = shellEstRowsLen > 0 || nozzleEstRowsLen > 0;

    console.log("[EstimationTab] auto-build check:", {
      currentRevision,
      revisionLoadVersion,
      dgv8Len,
      shellEstRowsLen,
      nozzleEstRowsLen,
    });

    if (dgv8Len > 0 || !hasEstimateInputs) {
      return;
    }

    const nextAutoBuildKey = `${estimationRefreshKey}-${shellEstRowsLen}-${nozzleEstRowsLen}`;
    if (autoBuildKeyRef.current === nextAutoBuildKey) {
      return;
    }

    autoBuildKeyRef.current = nextAutoBuildKey;
    console.log("[EstimationTab] auto-building DGV8 after revision load");
    getTableData();
  }, [
    currentRevision,
    estimationRefreshKey,
    revisionLoadVersion,
    watchedDgv8,
    watchedNozzleEstRows,
    watchedShellEstRows,
  ]);

  const handleSaveCell = (cell, value) => {
    setValue(`DataGridView8.${cell.row.index}.${cell.column.id}`, value);
  };

  const handleAttachmentCellChange = (rowIndex, field, value) => {
    setAttachmentRows((prev) => {
      const rows = [...prev];
      const row = rows[rowIndex] || { ...defaultAttachmentRow };
      rows[rowIndex] = { ...row, [field]: value };
      return rows;
    });
  };

  const handleAttachmentComponentChange = async (rowIndex, value) => {
    setAttachmentRows((prev) => {
      const rows = [...prev];
      const row = rows[rowIndex] || { ...defaultAttachmentRow };
      rows[rowIndex] = {
        ...row,
        component: value,
        reference: "",
        totalWeight: "",
        totalMaterialCost: "",
        totalManhours: "",
        referenceOptions: [],
      };
      return rows;
    });

    const config = attachmentDbConfig[value];
    if (!config) return;

    const tables = await loadPvTables();
    const table = tables && tables[config.table];
    if (!table || !Array.isArray(table)) return;

    const keyColumn = config.keyColumnMetric;
    const options = Array.from(
      new Set(
        table
          .map((r) => r && r[keyColumn])
          .filter(
            (v) =>
              v !== undefined &&
              v !== null &&
              String(v).trim() !== ""
          )
      )
    ).map((v) => String(v));

    setAttachmentRows((prev) => {
      const rows = [...prev];
      const row = rows[rowIndex] || { ...defaultAttachmentRow };
      rows[rowIndex] = {
        ...row,
        referenceOptions: options,
      };
      return rows;
    });
  };

  const handleAttachmentReferenceChange = async (
    rowIndex,
    component,
    value
  ) => {
    setAttachmentRows((prev) => {
      const rows = [...prev];
      const row = rows[rowIndex] || { ...defaultAttachmentRow };
      rows[rowIndex] = {
        ...row,
        reference: value,
      };
      return rows;
    });

    const config = attachmentDbConfig[component];
    if (!config) return;

    const tables = await loadPvTables();
    const table = tables && tables[config.table];
    if (!table || !Array.isArray(table)) return;

    const keyColumn = config.keyColumnMetric;
    const match = table.find(
      (r) => String(r[keyColumn]) === String(value)
    );
    if (!match) return;

    const weight = Number(match[config.weightColumnMetric]) || 0;
    const cost = Number(match[config.costColumnMetric]) || 0;
    const manhours = Number(match[config.manhoursColumn]) || 0;

    setAttachmentRows((prev) => {
      const rows = [...prev];
      const row = rows[rowIndex] || { ...defaultAttachmentRow };
      rows[rowIndex] = {
        ...row,
        totalWeight: weight,
        totalMaterialCost: cost,
        totalManhours: manhours,
      };
      return rows;
    });
  };

  const handleAddAttachmentRow = () => {
    setAttachmentRows((prev) => [...prev, { ...defaultAttachmentRow }]);
  };

  const handleDeleteAttachmentRow = (index) => {
    setAttachmentRows((prev) => prev.filter((_, i) => i !== index));
  };
  const initialDialogDetails = {
    open: false,
    title: "",
    description: "",
    columns: [],
    data: {},
    // compare cost , title, description, available table data
  };
  const [dialogDetails, setdialogDetails] = useState(initialDialogDetails);

  //create new row
  const handleCreateNewRow = (values) => {
    // tableData.push(values);
    console.log("appending");
    append({
      UID: "",
      Code: "code",
      Component: "Custom Item",
      Description: "Custom Item",
      Qty: 0,
      "Measure/Wt": 0,
      "Material Cost/ Unit": 0,
      "Material Cost": 0,
      "FAB Hours": 0,
      Remarks: "",
    });
    reRender();
  };

  //delete row
  const handleDeleteRow = (index) => {
    remove(index);
  };

  const handleShowBom = async () => {
    let data = getValues("DataGridView8") || [];
    console.log("[EstimationTab] handleShowBom initial DGV8:", data.length);

    if (!data || data.length === 0) {
      await getTableData();
      data = getValues("DataGridView8") || [];
      console.log("[EstimationTab] handleShowBom after getTableData:", data.length);
    }

    const newBom = buildBomFromDgv8(data);
    setValue("bomData", newBom);
    setBomData(newBom);
  };

  // ─── Send BOM to PLM ─────────────────────────────────────────────────────────
  function handleSendBomToPlm() {
    const bom = getValues("bomData") || [];

    const payload = {
      source: "Estimation",
      softwareId: uuidv4(),
      name: "Pressure Vessel",
      dwgNumber: "001",
      dwgTitle: "Pressure Vessel BOM",
      revision: "0",
      version: "673",
      sheetNumber: "0",
      sheetTitle: "Sheet",
      projectId: projectId,
      materials: bom.map((cur) => ({
        item: cur.itemNo,
        ref: null,
        description: cur.materialDescription,
        length:
          cur.dimension && cur.dimension !== "-"
            ? cur.dimension
            : null,
        quantity:
          cur.qty === "-"
            ? null
            : cur.qty,
        weightlb: cur.weightLb,
        costCode: null,
        softwareId: uuidv4(),
      })),
    };

    secondAxios.delete("drawing/title/Structure BOM");
    secondAxios.post("drawing", [payload]);
  }

  const handleClose = () => {
    setdialogDetails(initialDialogDetails);
  };

  const handleCompareCost = async (row) => {
    console.log(row);
    await getCostComparison(row?.Code).then((res) => {
      setdialogDetails({
        open: true,
        title: "Comparing Cost of Material in Past Orders",
        columns: costComparisonColumns,
        data: res || [],
      });
    });
    // navigate("/supply-chain-management#scm-tab=Warehouse&warehouse-tab=Materials%20Master")
  };
  const handleCheckStock = async (row) => {
    console.log(row);
    await getMaterialAvailability(row?.Code).then((res) => {
      setdialogDetails({
        open: true,
        title: "Availability of Material",
        columns: warehouseColumns,
        data: res || [],
      });
    });
    // navigate("/supply-chain-management#scm-tab=Warehouse&warehouse-tab=Inventory")
  };
  const getTableData = async () => {
    setIsLoading(true);
    try {
      // Run shell and nozzle estimation first to ensure data is fresh
      const formData = getValues();
      const projectTag = formData.TextBox48 || "";

      // Pre-populate any missing UIDs before sending to API
      let shellRows = formData.DataGridView5 || [];
      shellRows.forEach((row, i) => {
        if (!row.UID && row.Component) {
          row.UID = computeShellUID(row.Component, projectTag) || "";
        }
      });
      let nozzleRows = formData.DataGridView1 || [];
      nozzleRows.forEach((row, i) => {
        if (!row.UID && row.Mark) {
          row.UID = computeNozzleUID(row.Mark, projectTag) || "";
        }
      });

      if (typeof window !== "undefined" && window.electron?.pv) {
        const shellRes = await estimateShell(formData);
        if (shellRes && !shellRes.error) {
          if (shellRes.data.DataGridView7)
            setValue("DataGridView7", shellRes.data.DataGridView7);
          if (shellRes.data.DataGridView5) {
            // Preserve UIDs from original rows if the API response strips them
            const returnedShellRows = shellRes.data.DataGridView5;
            const mergedShellRows = returnedShellRows.map((row, i) => ({
              ...row,
              UID: shellRows[i]?.UID || computeShellUID(row.Component, projectTag) || "",
            }));
            setValue("DataGridView5", mergedShellRows);
          }
        }

        const nozzleRes = await estimateNozzle(formData);
        if (nozzleRes && !nozzleRes.error && nozzleRes.data) {
          // Preserve UIDs from original rows if the API response strips them
          const returnedNozzleRows = nozzleRes.data;
          const mergedNozzleRows = returnedNozzleRows.map((row, i) => ({
            ...row,
            UID: nozzleRows[i]?.UID || computeNozzleUID(row.Mark, projectTag) || "",
          }));
          setValue("DataGridView2", mergedNozzleRows);
        }
      }

      const allValues = getValues();
      const pvTables = await getPvCostTables();
      shellRows = allValues?.DataGridView5 || [];
      const shellEstRows = allValues?.DataGridView7 || [];
      const shellHeadItems = shellRows
        .filter((row) => row && row.Component)
        .map((row, idx) => {
          const thk = Number(row["Provided Thk"] || 0);
          const diaMm = Number(row["Diameter"] || 0);
          const lenMm = Number(row["Can Length"] || 0);
          const material = row.Material || "";
          const isShell =
            typeof row.Component === "string" &&
            row.Component.toLowerCase().includes("shell");
          const isHead =
            typeof row.Component === "string" &&
            row.Component.toLowerCase().includes("head");

          let reqQty = 1;
          if (isShell || isHead) {
            const diaM = diaMm / 1000;
            const lenM = (lenMm || diaMm) / 1000;

            let widthM, lengthM;
            if (isShell) {
              widthM = Math.PI * diaM;
              lengthM = lenM;
            } else {
              const blankDia = 1.084 * diaM + 2 * lenM;
              widthM = blankDia;
              lengthM = blankDia;
            }

            const widthStr = roundToTwo(widthM, 3);
            const lengthStr = roundToTwo(lengthM, 3);

            reqQty = `${widthStr} x ${lengthStr} x ${thk}`;
          }

          const est = shellEstRows[idx] || {};
          const fabHours =
            (parseFloat(est.Circ_Weld_Hours) || 0) +
            (parseFloat(est.Long_Weld_Hours) || 0) +
            (parseFloat(est.Rolling_Hours) || 0) +
            (parseFloat(est.Dishing_Hours) || 0);

          const description =
            thk || material
              ? `PLATE, ${toNearestPowerOfTwoFraction(thk)}" THK, ${material}`
              : "Custom Item";

          const materialComponentKey = isShell
            ? "Shell"
            : isHead
              ? "Head"
              : "Plate";
          const materialUnitCost = getMaterialCostFromTables(
            pvTables,
            materialComponentKey,
            material
          );
          const fabRate = getFabRateFromTables(
            pvTables,
            row.Component,
            null
          );

          return {
            UID: row.UID || "",
            Code: "",
            Component: row.Component,
            Description: description,
            Qty: reqQty,
            "Procured Qty": "",
            "Measure/Wt": row.Weight || 0,
            "Material Cost/ Unit": materialUnitCost,
            "Material Cost": 0,
            "FAB Hours": roundToTwo(fabHours),
            "FAB Rate/Hr": fabRate,
            Remarks: "",
          };
        });

      nozzleRows = allValues?.DataGridView1 || [];
      const nozzleEstRows = allValues?.DataGridView2 || [];
      const nozzleMaterial = getValues("ComboBox12");
      const flangeMaterial = getValues("ComboBox13");
      const padMaterial = getValues("ComboBox14");

      const pipeItems = [];
      const flangeItems = [];
      const padItems = [];

      nozzleRows.forEach((row, index) => {
        if (!row) return;
        const size = row.Size;
        const sch = row["Sch/Thk"] || "";
        const type = row.Type || "";
        const flangeRating = row["Flange Rating"] || "";
        const padOD = Number(row["Pad OD"] || 0);
        const padThk = Number(row["Pad Thk"] || 0);
        const estRow = nozzleEstRows[index] || {};
        const pipeWeight = safeNumber(estRow["Pipe Weight"]);
        const flangeWeight = safeNumber(estRow["Flange Weight"]);
        const padWeight = safeNumber(estRow["Pad Weight"]);
        const nozzleWeldHours = safeNumber(estRow["Nozzle Weld Hours"]);
        const pipeWeldHours = safeNumber(estRow["Pipe Weld Hours"]);
        const padWeldHours = safeNumber(estRow["Pad Weld Hours"]);
        const boltupHours = safeNumber(estRow["Bolt-up Hours"]);
        const pipeFabHours =
          nozzleWeldHours + pipeWeldHours + padWeldHours;
        const nozzleUID = row.UID || "";

        const pipeUnitCost = getPipeUnitCostFromTables(pvTables, size, sch);
        const flangeUnitCost = getFlangeUnitCostFromTables(
          pvTables,
          size,
          sch,
          flangeRating
        );

        const pipeFabRate = getFabRateFromTables(pvTables, "Pipe", size);
        const flangeFabRate = getFabRateFromTables(pvTables, "Flange", size);
        const padFabRate = getFabRateFromTables(pvTables, "Pad", size);

        if (size) {
          const numericSize = Number(size);
          const pipeDesc =
            numericSize && numericSize < 18
              ? `PIPE, ${numericSize}" SCH. ${sch}, ${nozzleMaterial || ""}`
              : `PLATE ${"1/2"}" THK, ${nozzleMaterial || ""}`;

          pipeItems.push({
            UID: nozzleUID,
            Code: "",
            Component: "Pipe",
            Description: pipeDesc,
            Qty: 1,
            "Procured Qty": "",
            "Measure/Wt": pipeWeight,
            "Material Cost/ Unit": pipeUnitCost,
            "Material Cost": 0,
            "FAB Hours": pipeFabHours,
            "FAB Rate/Hr": pipeFabRate,
            Remarks: "",
          });
        }

        if (flangeRating !== "-" && type !== "-") {
          const flangeDesc = `FLANGE, ${Number(
            size
          )}", Class ${flangeRating} - Sch. ${sch}, ${type}, ${
            flangeMaterial || ""
          }`;
          flangeItems.push({
            UID: nozzleUID,
            Code: "",
            Component: "Flange",
            Description: flangeDesc,
            Qty: 1,
            "Procured Qty": "",
            "Measure/Wt": flangeWeight,
            "Material Cost/ Unit": flangeUnitCost,
            "Material Cost": 0,
            "FAB Hours": boltupHours,
            "FAB Rate/Hr": flangeFabRate,
            Remarks: "",
          });
        }

        if (!(padOD === 0 && padThk === 0)) {
          const padDesc = `PAD, OD ${padOD} mm, THK ${padThk} mm, ${
            padMaterial || ""
          }`;

          padItems.push({
            UID: nozzleUID,
            Code: "",
            Component: "Pad",
            Description: padDesc,
            Qty: 1,
            "Procured Qty": "",
            "Measure/Wt": padWeight,
            "Material Cost/ Unit": 0,
            "Material Cost": 0,
            "FAB Hours": 0,
            "FAB Rate/Hr": padFabRate,
            Remarks: "",
          });
        }
      });

      const groupedNozzleItems = groupNozzleItems([
        ...pipeItems,
        ...flangeItems,
        ...padItems,
      ]);

      const estimationRows = [...shellHeadItems, ...groupedNozzleItems];

      setValue("DataGridView8", estimationRows);
      updateTableData();
    } finally {
      setIsLoading(false);
    }
  };

  //rounds numbers to n(default - two) decimals
  function roundToTwo(num, decimalPlaces = 2) {
    num = Math.round(num + "e" + decimalPlaces);
    return Number(num + "e" + -decimalPlaces);
  }

  const updateTableData = () => {
    const data = getValues("DataGridView8");
    console.log("[EstimationTab] updateTableData:", { dataLen: data?.length });
    //call the estimateTable function from backend
    // const getTableData = async () => {
    //   const data = getValues();
    //   setIsLoading(true);
    //   const result = await axios
    //     .post("/pressure_vessel/estimateAll", data)
    //     .then((response) => {
    //       console.log("result", response.data);
    //       setValue("DataGridView8", response.data.data);
    //       setValue("TextBox41", response.data.cost);
    //       setIsLoading(false);

    //       return response;
    //     })
    //     .catch((err) => {
    //       console.log(err);
    //     });
    //   updateTableData();
    // };

    //update the material cost
    const newValues = data.map((elem) => {
      const fabHours = roundToTwo(parseFloat(elem["FAB Hours"]) || 0);

      const isPipeOrFlange =
        typeof elem.Component === "string" &&
        (elem.Component.toLowerCase().includes("pipe") ||
          elem.Component.toLowerCase().includes("flange"));

      const qty = parseFloat(elem.Qty) || 0;
      const measure = parseFloat(elem["Measure/Wt"]) || 0;
      const unitCost = parseFloat(elem["Material Cost/ Unit"]) || 0;

      const materialCost = roundToTwo(
        isPipeOrFlange ? qty * unitCost : measure * unitCost
      );

      const rowFabRate =
        parseFloat(elem["FAB Rate/Hr"]) || parseFloat(getValues("TextBox35")) || 0;
      const fabCost =
        rowFabRate > 0 ? roundToTwo(fabHours * rowFabRate) : 0;

      return {
        ...elem,
        "FAB Hours": fabHours,
        "FAB Rate/Hr": rowFabRate,
        "FAB Cost": fabCost,
        "Material Cost": materialCost,
      };
    });

    console.log("calculated: ", newValues);
    setValue("DataGridView8", newValues);

    // reRender()
    //update the total material cost and total fab hours in table
    const totals = newValues?.reduce(
      (accumulator, currentValue) => [
        accumulator[0] + parseFloat(currentValue["Material Cost"]),
        accumulator[1] + parseFloat(currentValue["FAB Hours"]),
        accumulator[2] + parseFloat(currentValue["FAB Cost"]),
      ],
      [0.0, 0.0, 0.0]
    );

    console.log("calculated: ", newValues);
    setValue("DataGridView8", newValues);

    // reRender()

    //update the Fab Cost in the small box
    const fabRate = getValues("TextBox35");
    let fabcost = totals[1] * fabRate;
    setValue("TextBox41", fabcost);

    //to keep the values in store - make it part of the react hook form
    const isRegistered = Object.keys(errors).some(
      (error) => error === "totalCost"
    );
    if (!isRegistered) {
      register("totalMaterialCost");
      register("totalCost");
      register("totalHours");
      register("totalFabCost");
    }
    setValue("totalMaterialCost", roundToTwo(totals[0]));
    setValue("totalHours", roundToTwo(totals[1]));
    setValue("totalFabCost", roundToTwo(totals[2]));

    //update the total Cost
    setValue("totalCost", roundToTwo(totals[0] + totals[2]));
    console.log("[EstimationTab] updateTableData totals:", {
      totalMaterialCost: roundToTwo(totals[0]),
      totalHours: roundToTwo(totals[1]),
      totalFabCost: roundToTwo(totals[2]),
    });
    reRender();
  };

  const handleGenerateAttachmentsBom = () => {
    const bom = [];
    const rows = attachmentRows || [];

    rows.forEach((row) => {
      if (!row || !row.component) {
        return;
      }

      const description = row.component;
      const qty = Number(row.qty) || 0;
      const weight = Number(row.totalWeight) || 0;
      const cost = Number(row.totalMaterialCost) || 0;
      const upperDescription = description.toUpperCase();

      bom.push({
        costCode: upperDescription,
        description,
        qty,
        totalWeight: qty * weight,
        totalCost: qty * cost,
      });
    });

    setAttachmentBomRows(bom);
  };

  const attachmentTotals = attachmentBomRows.reduce(
    (acc, row) => {
      return {
        qty: acc.qty + (Number(row.qty) || 0),
        totalWeight: acc.totalWeight + (Number(row.totalWeight) || 0),
        totalCost: acc.totalCost + (Number(row.totalCost) || 0),
      };
    },
    { qty: 0, totalWeight: 0, totalCost: 0 }
  );

  const mainEstimationPanel = (
    <div style={{ width: "100%", minWidth: "900px", padding: "10px" }}>
      <CustomAlert
        open={openAlert}
        setOpen={setOpenAlert}
        severity={AlertContent.severity}
        message={AlertContent.message}
      />
      <Stack spacing={2} direction="row" justifyContent="space-between" alignItems="center">
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            color="secondary"
            onClick={() => getTableData()}
            variant="contained"
          >
            Estimate
          </Button>
          {/* <Button
            color="secondary"
            onClick={() => updateTableData()}
            variant="contained"
          >
            Update
          </Button> */}
          <Button
            color="secondary"
            onClick={handleShowBom}
            variant="contained"
          >
            BOM
          </Button>
        </Box>
        <Button variant="contained" onClick={handleSendBomToPlm}>
          SEND BOM TO PLM
        </Button>
      </Stack>
      <br />
      <Grid container rowSpacing={1} spacing={1} columns={12}>
        <Grid item xs={12}>
          <EstimationTable
            rows={estimationTableRows}
            reRender={reRender}
            handleSaveCell={handleSaveCell}
            handleCreateNewRow={handleCreateNewRow}
            handleDeleteRow={handleDeleteRow}
            isLoading={isLoading}
            getValues={getValues}
            handleCompareCost={handleCompareCost}
            handleCheckStock={handleCheckStock}
            isEditTable={isEditTable}
            setIsEditTable={setIsEditTable}
          />
        </Grid>
      </Grid>
      <br />
      <Grid container rowSpacing={1} spacing={1} columns={12}>
        <Grid item xs={12} md={4}>
          <SummaryBox data={summaryData} />
        </Grid>
        <Grid item xs={12} md={8}>
          <BomTable data={bomData} />
        </Grid>
      </Grid>
      <DetailsDialog dialogDetails={dialogDetails} handleClose={handleClose} />
      <br />
    </div>
  );

  const attachmentPanel = (
    <div style={{ width: "100%", minWidth: "900px", padding: "10px" }}>
      <Stack spacing={2} direction="row">
        <Button
          color="secondary"
          variant="contained"
          onClick={handleGenerateAttachmentsBom}
        >
          Generate BOM
        </Button>
        <Button
          color="secondary"
          variant="outlined"
          onClick={handleAddAttachmentRow}
        >
          Add row
        </Button>
      </Stack>
      <br />
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <MaterialReactTable
            columns={getAttachmentColumns({
              handleAttachmentComponentChange,
              handleAttachmentReferenceChange,
              handleAttachmentCellChange,
            })}
            data={attachmentRows}
            enableColumnActions={false}
            enableTopToolbar={false}
            enableBottomToolbar={false}
            enableRowActions
            positionActionsColumn="last"
            {...attachmentTableProps}
            muiTableBodyCellProps={{
              sx: {
                padding: "8.5px 8px",
                textAlign: "center",
                borderRight: "1px solid rgba(224,224,224,1)",
              },
            }}
            displayColumnDefOptions={{
              "mrt-row-actions": {
                size: 5,
                muiTableHeadCellProps: {
                  align: "center",
                },
                muiTableBodyCellProps: {
                  align: "center",
                  sx: {
                    p: 0,
                    "& button": {
                      ml: 0,
                    },
                  },
                },
              },
            }}
            renderRowActionMenuItems={({ row }) => [
              <MenuItem
                key={0}
                sx={{ m: 0 }}
                onClick={() => handleDeleteAttachmentRow(row.index)}
              >
                <IconButton size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </MenuItem>,
            ]}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          {attachmentBomRows.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cost Code</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Qty</TableCell>
                  <TableCell>Total Weight (kg)</TableCell>
                  <TableCell>Total Cost (US $)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attachmentBomRows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.costCode}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>{row.qty}</TableCell>
                    <TableCell>{row.totalWeight}</TableCell>
                    <TableCell>{row.totalCost}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell>Total</TableCell>
                  <TableCell />
                  <TableCell>{attachmentTotals.qty}</TableCell>
                  <TableCell>{attachmentTotals.totalWeight}</TableCell>
                  <TableCell>{attachmentTotals.totalCost}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </Grid>
      </Grid>
    </div>
  );

  const tabs = [
    {
      label: "Main Estimation",
      panel: mainEstimationPanel,
    },
    {
      label: "Attachment",
      panel: attachmentPanel,
    },
  ];

  console.log({ summaryData });

  return (
    <NavTabs
      navLabel="pv-estimation-sub"
      tabs={tabs}
    />
  );
};

export default EstimationTab;
